import React from 'react';
import { GripVertical, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

interface DashboardModuleProps {
  id: string;
  title: string;
  icon?: React.ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onViewAll?: () => void;
  viewAllLabel?: string;
  children: React.ReactNode;
  className?: string;
  dragHandleProps?: Record<string, unknown>;
  badge?: React.ReactNode;
}

const DashboardModule: React.FC<DashboardModuleProps> = ({
  title,
  icon,
  collapsed = false,
  onToggleCollapse,
  onViewAll,
  viewAllLabel = 'View All',
  children,
  className = '',
  dragHandleProps,
  badge,
}) => {
  return (
    <div className={`eg-card min-w-0 ${className}`}>
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: collapsed ? 'none' : '1px solid var(--color-border)' }}
      >
        {dragHandleProps && (
          <button
            className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
            {...dragHandleProps}
          >
            <GripVertical className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        )}

        {icon && <span className="flex-shrink-0">{icon}</span>}

        <h3
          className="text-sm font-semibold uppercase tracking-wider flex-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {title}
        </h3>

        {badge}

        {onViewAll && (
          <button
            onClick={onViewAll}
            className="flex items-center gap-1 text-xs font-medium text-[#17c480] hover:text-[#17c480]/80 transition-colors"
          >
            {viewAllLabel}
            <ArrowRight className="w-3 h-3" />
          </button>
        )}

        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            {collapsed ? (
              <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
            ) : (
              <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
            )}
          </button>
        )}
      </div>

      {/* Body */}
      <div
        className="transition-all duration-200 ease-in-out overflow-hidden"
        style={{ maxHeight: collapsed ? 0 : '2000px', opacity: collapsed ? 0 : 1 }}
      >
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

export default DashboardModule;
